import React, { useRef, useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import {
    FaPaperclip, FaImage, FaVideo, FaMicrophone, FaFile, FaTimes,
    FaFileAlt, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint,
    FaStop, FaDownload, FaPlay, FaPause, FaExpand,
} from "react-icons/fa";
import { ChatMessage, MessageType } from "../../context/ChatContext";

function baseUrl(): string {
    return (process.env.REACT_APP_API_URL || window.location.origin).replace(/\/$/, "");
}

/* ── Helpers ── */

function formatFileSize(bytes: number | null): string {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mime: string | null) {
    if (!mime) return <FaFileAlt size={20} />;
    if (mime.includes("pdf")) return <FaFilePdf size={20} className="text-red-500" />;
    if (mime.includes("word") || mime.includes("document")) return <FaFileWord size={20} className="text-blue-600" />;
    if (mime.includes("excel") || mime.includes("spreadsheet")) return <FaFileExcel size={20} className="text-green-600" />;
    if (mime.includes("powerpoint") || mime.includes("presentation")) return <FaFilePowerpoint size={20} className="text-orange-500" />;
    return <FaFileAlt size={20} className="text-gray-500" />;
}

/* ── VoiceNotePlayer: WhatsApp-style inline audio player ── */

function fmtDuration(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
}

const VoiceNotePlayer: React.FC<{ url: string; mine: boolean; size: string }> = ({ url, mine, size }) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const barRef = useRef<HTMLDivElement>(null);
    const [playing, setPlaying] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const rafRef = useRef<number>(0);

    // Auto-preload when URL is available (WhatsApp-style)
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const onMeta = () => { setDuration(el.duration); setLoaded(true); };
        const onEnd = () => { setPlaying(false); setCurrent(0); el.currentTime = 0; };
        const onErr = () => setLoaded(false);
        el.addEventListener("loadedmetadata", onMeta);
        el.addEventListener("ended", onEnd);
        el.addEventListener("error", onErr);
        // preload="auto" on the element triggers the download immediately
        return () => {
            el.removeEventListener("loadedmetadata", onMeta);
            el.removeEventListener("ended", onEnd);
            el.removeEventListener("error", onErr);
        };
    }, [url]);

    // RAF loop for progress
    useEffect(() => {
        if (playing) {
            const tick = () => {
                if (audioRef.current) setCurrent(audioRef.current.currentTime);
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);
        }
        return () => cancelAnimationFrame(rafRef.current);
    }, [playing]);

    const togglePlay = () => {
        const el = audioRef.current;
        if (!el) return;
        if (playing) {
            el.pause();
            setPlaying(false);
        } else {
            el.play().then(() => setPlaying(true)).catch(() => {});
        }
    };

    const seek = (e: React.MouseEvent<HTMLDivElement>) => {
        const bar = barRef.current;
        const el = audioRef.current;
        if (!bar || !el) return;
        const dur = el.duration;
        // Guard: duration must be a finite, positive number before we can seek
        if (!Number.isFinite(dur) || dur <= 0) return;
        const rect = bar.getBoundingClientRect();
        const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        el.currentTime = frac * dur;
        setCurrent(el.currentTime);
    };

    const progress = duration > 0 ? (current / duration) * 100 : 0;
    const displayDur = loaded ? fmtDuration(duration) : size;
    const displayCur = loaded ? fmtDuration(current) : "";

    const barColor = mine ? "bg-white/50" : "bg-gray-300";
    const fillColor = mine ? "bg-white" : "bg-primary";
    const thumbColor = mine ? "bg-white" : "bg-primary";

    return (
        <div className={`flex items-center gap-2.5 min-w-[180px] max-w-[220px] ${mine ? "pl-1" : ""}`}>
            {/* Hidden audio element — preload="auto" downloads immediately */}
            <audio ref={audioRef} src={url} preload="auto" className="hidden" />

            <button
                type="button"
                onClick={togglePlay}
                className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition ${
                    mine
                        ? "bg-white/20 text-white hover:bg-white/30"
                        : "bg-primary/10 text-primary hover:bg-primary/20"
                }`}
                aria-label={playing ? "Pause" : "Play"}
            >
                {playing ? <FaPause size={10} /> : <FaPlay size={10} className="ml-0.5" />}
            </button>

            <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                {/* Progress bar — clickable to seek */}
                <div
                    ref={barRef}
                    onClick={seek}
                    className={`h-1.5 rounded-full cursor-pointer relative overflow-hidden ${barColor}`}
                >
                    <div
                        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-100 ${fillColor}`}
                        style={{ width: `${progress}%` }}
                    />
                    <div
                        className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full ${thumbColor} shadow transition-all duration-100`}
                        style={{ left: `calc(${progress}% - 5px)` }}
                    />
                </div>

                {/* Time display */}
                <div className={`flex items-center justify-between text-[10px] ${mine ? "text-white/60" : "text-gray-400"}`}>
                    <span>{displayCur || displayDur}</span>
                    {loaded && (
                        <FaMicrophone size={9} className={mine ? "text-white/40" : "text-gray-300"} />
                    )}
                </div>
            </div>
        </div>
    );
};

/* ── MediaBubble: Renders the content of a media message ── */

export const MediaBubble: React.FC<{
    message: ChatMessage;
    mine: boolean;
}> = ({ message, mine }) => {
    const url = message.attachmentUrl;
    const name = message.attachmentName || "file";
    const size = formatFileSize(message.attachmentSize);
    const type = message.messageType;

    if (!url) {
        return <div className="text-xs text-gray-400 italic">Attachment unavailable</div>;
    }

    if (type === "image") {
        return (
            <div className="relative group">
                <img
                    src={url}
                    alt={name}
                    className="max-w-full rounded-lg cursor-pointer max-h-[240px] object-cover"
                    loading="lazy"
                    onClick={() => window.open(url, "_blank")}
                />
                <button
                    onClick={() => window.open(url, "_blank")}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                    title="View full size"
                >
                    <FaExpand size={10} />
                </button>
            </div>
        );
    }

    if (type === "video") {
        return (
            <video
                src={url}
                controls
                preload="metadata"
                className="max-w-full rounded-lg max-h-[240px]"
            />
        );
    }

    if (type === "audio") {
        return <VoiceNotePlayer url={url!} mine={mine} size={size} />;
    }

    // Document
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 p-2 rounded-lg transition ${
                mine
                    ? "bg-white/10 hover:bg-white/20"
                    : "bg-gray-50 hover:bg-gray-100 border border-gray-200"
            }`}
        >
            <div className="shrink-0">{fileIcon(message.attachmentMime)}</div>
            <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold truncate ${mine ? "text-white" : "text-gray-800"}`}>
                    {name}
                </div>
                <div className={`text-[10px] ${mine ? "text-white/60" : "text-gray-400"}`}>
                    {size}
                </div>
            </div>
            <FaDownload size={12} className={mine ? "text-white/50" : "text-gray-400"} />
        </a>
    );
};

/* ── AttachmentPicker: The + button and menu ── */

interface PickerProps {
    onFilePicked: (file: File, type: MessageType) => void;
    disabled?: boolean;
    /** Compact mode for the customer widget */
    compact?: boolean;
}

const ACCEPT_MAP: Record<string, string> = {
    image: "image/jpeg,image/png,image/gif,image/webp,image/svg+xml",
    video: "video/mp4,video/webm,video/quicktime",
    audio: "audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/webm,audio/aac,audio/mp4,audio/flac,audio/amr",
    document: "application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar",
};

export const AttachmentPicker: React.FC<PickerProps> = ({ onFilePicked, disabled, compact }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const fileRef = useRef<HTMLInputElement>(null);
    const currentAccept = useRef("");
    const currentType = useRef<MessageType>("document");
    const btnRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Recalculate position when menu opens
    const updatePosition = useCallback(() => {
        if (!btnRef.current) return;
        const rect = btnRef.current.getBoundingClientRect();
        // Position the menu above the button, aligned to its left edge
        setMenuPos({
            top: rect.top - 4, // 4px gap above the button
            left: rect.left,
        });
    }, []);

    useEffect(() => {
        if (!menuOpen) return;
        updatePosition();
        const handleClick = (e: MouseEvent) => {
            if (
                menuRef.current && !menuRef.current.contains(e.target as Node) &&
                btnRef.current && !btnRef.current.contains(e.target as Node)
            ) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        window.addEventListener("resize", updatePosition);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            window.removeEventListener("resize", updatePosition);
        };
    }, [menuOpen, updatePosition]);

    const pickFile = (accept: string, type: MessageType) => {
        currentAccept.current = accept;
        currentType.current = type;
        if (fileRef.current) {
            fileRef.current.accept = accept;
            fileRef.current.click();
        }
        setMenuOpen(false);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            onFilePicked(file, currentType.current);
        }
        e.target.value = "";
    };

    // The menu height: 4 items × ~44px each + padding ≈ 188px
    const MENU_HEIGHT = 192;

    return (
        <>
            <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                tabIndex={-1}
            />
            <button
                ref={btnRef}
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                disabled={disabled}
                className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-full flex items-center justify-center transition shrink-0
                    ${menuOpen
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                    }
                    disabled:opacity-40`}
                aria-label="Attach file"
                title="Attach file"
            >
                {menuOpen ? <FaTimes size={compact ? 12 : 14} /> : <FaPaperclip size={compact ? 12 : 14} />}
            </button>
            {menuOpen && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    className="fixed z-[9999] bg-white rounded-xl shadow-xl border border-gray-200 py-1.5 min-w-[180px] animate-[fadeIn_.15s_ease-out]"
                    style={{
                        top: menuPos.top - MENU_HEIGHT,
                        left: menuPos.left,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => pickFile(ACCEPT_MAP.image, "image")}
                        className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                        <FaImage className="text-emerald-500" /> Photo
                    </button>
                    <button
                        type="button"
                        onClick={() => pickFile(ACCEPT_MAP.video, "video")}
                        className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                        <FaVideo className="text-purple-500" /> Video
                    </button>
                    <button
                        type="button"
                        onClick={() => pickFile(ACCEPT_MAP.audio, "audio")}
                        className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                        <FaMicrophone className="text-red-500" /> Audio
                    </button>
                    <button
                        type="button"
                        onClick={() => pickFile(ACCEPT_MAP.document, "document")}
                        className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 hover:bg-gray-50 transition"
                    >
                        <FaFile className="text-blue-500" /> Document
                    </button>
                </div>,
                document.body,
            )}
        </>
    );
};

/* ── AudioRecorder: Hold-to-record or tap-to-record voice message ── */

interface AudioRecorderProps {
    onRecorded: (blob: Blob) => void;
    disabled?: boolean;
    compact?: boolean;
    /** Called when recording starts/stops so the parent can adjust layout */
    onRecordingChange?: (active: boolean) => void;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({ onRecorded, disabled, compact, onRecordingChange }) => {
    const [recording, setRecording] = useState(false);
    const [duration, setDuration] = useState(0);
    const mediaRecorder = useRef<MediaRecorder | null>(null);
    const chunks = useRef<Blob[]>([]);
    const timerRef = useRef<any>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const notifyParent = (active: boolean) => {
        setRecording(active);
        onRecordingChange?.(active);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // Prefer MP4/AAC (WhatsApp-compatible), fall back to webm/opus
            const mimeType = MediaRecorder.isTypeSupported("audio/mp4")
                ? "audio/mp4"
                : MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                    ? "audio/webm;codecs=opus"
                    : MediaRecorder.isTypeSupported("audio/webm")
                        ? "audio/webm"
                        : "audio/ogg";

            const recorder = new MediaRecorder(stream, { mimeType });
            mediaRecorder.current = recorder;
            chunks.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.current.push(e.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(chunks.current, { type: recorder.mimeType });
                if (blob.size > 0) onRecorded(blob);
                chunks.current = [];
                stream.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            };

            recorder.start(250);
            notifyParent(true);
            setDuration(0);
            timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
        } catch (err) {
            console.error("[AudioRecorder] microphone access denied:", err);
            alert("Microphone access is required to record audio.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
            mediaRecorder.current.stop();
        }
        clearInterval(timerRef.current);
        notifyParent(false);
        setDuration(0);
    };

    const cancelRecording = () => {
        if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
            mediaRecorder.current.ondataavailable = null;
            mediaRecorder.current.onstop = null;
            mediaRecorder.current.stop();
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        clearInterval(timerRef.current);
        chunks.current = [];
        notifyParent(false);
        setDuration(0);
    };

    useEffect(() => {
        return () => {
            clearInterval(timerRef.current);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
        };
    }, []);

    const fmtDuration = (s: number) => {
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${sec.toString().padStart(2, "0")}`;
    };

    if (recording) {
        return (
            <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-xs text-red-500 font-mono font-semibold">{fmtDuration(duration)}</span>
                </span>
                <button
                    type="button"
                    onClick={cancelRecording}
                    className="w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 flex items-center justify-center transition"
                    title="Cancel"
                >
                    <FaTimes size={11} />
                </button>
                <button
                    type="button"
                    onClick={stopRecording}
                    className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition`}
                    title="Stop & send"
                >
                    <FaStop size={compact ? 11 : 13} />
                </button>
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className={`${compact ? "w-9 h-9" : "w-10 h-10"} rounded-full bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 hover:text-red-600 flex items-center justify-center transition disabled:opacity-40 shrink-0`}
            title="Record voice message"
            aria-label="Record voice message"
        >
            <FaMicrophone size={compact ? 14 : 16} />
        </button>
    );
};

/* ── AttachmentPreview: Shows a preview of the file being uploaded ── */

interface PreviewProps {
    file: File;
    type: MessageType;
    uploading: boolean;
    progress?: number;
    onCancel: () => void;
}

export const AttachmentPreview: React.FC<PreviewProps> = ({ file, type, uploading, progress, onCancel }) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (type === "image" || type === "video") {
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
    }, [file, type]);

    return (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 flex items-center gap-3">
            {/* Preview thumbnail */}
            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center overflow-hidden shrink-0">
                {type === "image" && previewUrl ? (
                    <img src={previewUrl} alt="" className="w-full h-full object-cover" />
                ) : type === "video" && previewUrl ? (
                    <video src={previewUrl} className="w-full h-full object-cover" />
                ) : type === "audio" ? (
                    <FaMicrophone size={18} className="text-gray-500" />
                ) : (
                    fileIcon(file.type)
                )}
            </div>

            <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-800 truncate">{file.name}</div>
                <div className="text-[10px] text-gray-400">{formatFileSize(file.size)}</div>
                {uploading && (
                    <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${progress ?? 50}%` }}
                        />
                    </div>
                )}
            </div>

            {!uploading && (
                <button
                    type="button"
                    onClick={onCancel}
                    className="w-7 h-7 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-500 transition"
                    title="Remove"
                >
                    <FaTimes size={10} />
                </button>
            )}
        </div>
    );
};

/* ── Upload helper ── */

export async function uploadFileToS3(opts: {
    file: File;
    roomId?: number;
    messageType: MessageType;
}): Promise<{ key: string; messageType: MessageType }> {
    // 1. Get presigned upload URL from our server
    const res = await fetch(`${baseUrl()}/chat/upload`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            roomId: opts.roomId,
            fileName: opts.file.name,
            fileSize: opts.file.size,
            mimeType: opts.file.type,
        }),
    });

    if (!res.ok) {
        const data = await res.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(data.error || "Failed to get upload URL");
    }

    const { uploadUrl, key, messageType } = await res.json();

    // 2. Upload file directly to S3 via presigned PUT.
    // NOTE: Content-Length is a forbidden header in the Fetch API —
    // the browser sets it automatically from the body. Setting it
    // manually is silently ignored and would cause a signature
    // mismatch if the presigned URL were signed with it.
    const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
            "Content-Type": opts.file.type,
        },
        body: opts.file,
    });

    if (!uploadRes.ok) {
        throw new Error("Failed to upload file to S3");
    }

    return { key, messageType };
}
